import React, { useState, useRef, useEffect, useCallback } from "react";
import { fromTable } from "@/integrations/supabase/db";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  User, MapPin, TrendingUp, Star, Target, Compass, Shield, AlertTriangle,
  Heart, Wallet, Home, GraduationCap, Pencil, X, Maximize2, Minimize2, GripHorizontal
} from "lucide-react";
import type { BeratungSeite } from "@/hooks/useBeratungSeiten";
import { cn } from "@/lib/utils";
import { useFullscreen } from "@/hooks/useFullscreen";
import { DrawingCanvas, DrawingContent } from "./DrawingCanvas";
import {
  RankingVisualization,
  InsuranceWheelChart,
  TimelineVisualization,
  GapAnalysisChart,
  SummaryCards,
  PriorityTiles,
  PriorityTilesEditor,
  ServiceWorldMap,
  RentTimelineChart,
  FinancingPyramid,
  BenefitChecklist,
  PensionGapChart,
  RiskProfileChart,
  LifecycleTimeline,
  IncomeValueCalculator,
  SignaturePanel,
  FeedbackRequest,
  SavingsComparisonChart,
  GenerationalRatioChart,
  InvestmentProfileSelector,
  WorkforceValueDisplay,
  ChildProvisionChart,
  RetirementPlanningView,
  InvestmentView,
  HouseholdBudgetView,
  PropertyInsuranceView,
  IncomeProtectionView,
  ReferralFeedbackView,
} from "./visualizations";
import { KundenAnsichtCard } from "./components/KundenAnsichtCard";

const ICON_MAP: Record<string, React.ElementType> = {
  User, MapPin, TrendingUp, Star, Target, Compass, Shield, AlertTriangle,
  Heart, Wallet, Home, GraduationCap
};

interface KundenPanelProps {
  seite: BeratungSeite | null;
  formValues: Record<string, any>;
  /** Callback for form value changes (for persistence) */
  onFieldChange?: (fieldId: string, value: any) => void;
  drawingContent?: DrawingContent;
  isDrawingShared?: boolean;
  isDrawingCollaborative?: boolean;
  onDrawingChange?: (content: DrawingContent) => void;
  /** 'popover' (default) = floating overlay, 'inline' = replaces visualization */
  drawingPlacement?: 'popover' | 'inline';
  /** Fullscreen mode - removes padding and borders for standalone view */
  fullscreen?: boolean;
  /** Show header even in fullscreen mode */
  showHeader?: boolean;
  /** Whether customer can interactively select priority tiles */
  interactiveTiles?: boolean;
  /** Callback when a priority tile is clicked */
  onTileSelect?: (tileId: string) => void;
  /** Currently hovered tile ID (for cross-view sync) */
  hoveredTileId?: string | null;
  /** Callback when tile hover state changes */
  onTileHover?: (tileId: string | null) => void;
  /** Admin edit mode for editing tiles, layout etc. */
  isEditModeActive?: boolean;
  /** Callback when tiles are updated in edit mode */
  onTilesUpdate?: (tiles: any[]) => void;
}

export function KundenPanel({ 
  seite, 
  formValues, 
  onFieldChange,
  drawingContent,
  isDrawingShared = false,
  isDrawingCollaborative = false,
  onDrawingChange,
  drawingPlacement = 'popover',
  fullscreen = false,
  showHeader = false,
  interactiveTiles = false,
  onTileSelect,
  hoveredTileId,
  onTileHover,
  isEditModeActive = false,
  onTilesUpdate,
}: KundenPanelProps) {
  const [isDrawingExpanded, setIsDrawingExpanded] = useState(true);
  const [isDrawingDismissed, setIsDrawingDismissed] = useState(false);
  
  // Drag state - centered by default (0,0 offset from center)
  const [drawingPosition, setDrawingPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Fullscreen support
  const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef);

  // Reset dismissed state when sharing is toggled on
  useEffect(() => {
    if (isDrawingShared) {
      setIsDrawingDismissed(false);
      setDrawingPosition({ x: 0, y: 0 }); // Reset to center
    }
  }, [isDrawingShared]);

  // Drag handlers
  const handleDragStart = (e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: drawingPosition.x,
      posY: drawingPosition.y
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handleDrag = (e: React.PointerEvent) => {
    if (!isDragging) return;
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    setDrawingPosition({
      x: dragStartRef.current.posX + deltaX,
      y: dragStartRef.current.posY + deltaY
    });
  };

  const handleDragEnd = (e: React.PointerEvent) => {
    setIsDragging(false);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  };

  if (!seite) {
    return (
      <div ref={containerRef} className="h-full flex items-center justify-center text-muted-foreground bg-muted/30 relative">
        Wählen Sie Module aus und starten Sie die Beratung
      </div>
    );
  }

  const layout = seite.kunden_layout;
  const beraterLayout = seite.berater_layout;
  const IconComponent = layout.icon ? ICON_MAP[layout.icon] : null;

  // Get ranking data from form values
  const rankingKey = `ranking_${seite.id}`;
  const rankingOrder = formValues[rankingKey];

  // Extract selected priorities for priority-tiles visualization
  const getSelectedPriorities = (): string[] => {
    // Look for priority/priorität fields in formValues
    const priorityKeys = Object.keys(formValues).filter(
      key => key.toLowerCase().includes('priorit') || key.toLowerCase().includes('priority')
    );
    const selected: string[] = [];
    priorityKeys.forEach(key => {
      const val = formValues[key];
      if (Array.isArray(val)) {
        selected.push(...val);
      } else if (val) {
        selected.push(val);
      }
    });
    return selected;
  };

  const renderVisualization = () => {
    switch (layout.type) {
      case 'summary':
        return (
          <SummaryCards
            formValues={formValues}
            icon={IconComponent || undefined}
            description={layout.description}
          />
        );

      case 'ranking-visual':
        const rankingItems = beraterLayout.type === 'ranking' && beraterLayout.items
          ? beraterLayout.items
          : [];
        
        return (
          <RankingVisualization
            items={rankingItems}
            orderedIds={rankingOrder}
          />
        );

      case 'timeline':
        return (
          <TimelineVisualization
            formValues={formValues}
            timeframe={layout.timeframe}
          />
        );

      case 'insurance-wheel':
        const insuranceItems = beraterLayout.type === 'checklist' && beraterLayout.items
          ? beraterLayout.items
          : [];
        
        return (
          <InsuranceWheelChart
            categories={insuranceItems}
            formValues={formValues}
          />
        );

      case 'chart':
        return (
          <GapAnalysisChart
            formValues={formValues}
          />
        );

      case 'priority-tiles':
        // Use reusable KundenAnsichtCard component
        return (
          <KundenAnsichtCard
            seite={seite}
            formValues={formValues}
            interactiveTiles={interactiveTiles}
            onTileSelect={onTileSelect}
            hoveredTileId={hoveredTileId}
            onTileHover={onTileHover}
            isEditModeActive={isEditModeActive}
            onTilesUpdate={onTilesUpdate}
            className="border-none shadow-none"
          />
        );

      case 'service-map':
        return (
          <ServiceWorldMap
            title={layout.title}
            subtitle={layout.subtitle}
            services={layout.services || []}
            animated={layout.animated !== false}
          />
        );

      case 'rent-timeline':
        return (
          <RentTimelineChart
            startMiete={formValues.miete ?? layout.startMiete ?? 1000}
            steigerung={formValues.miet_steigerung ?? layout.steigerung ?? 0.03}
            jahre={formValues.mietjahre ?? layout.jahre ?? 35}
            illustrationUrl={layout.illustrationUrl}
            isEditMode={isEditModeActive}
            onFieldChange={onFieldChange}
            onIllustrationChange={async (url) => {
              try {
                const updatedKundenLayout = {
                  ...layout,
                  illustrationUrl: url,
                };

                await fromTable('beratung_seiten')
                  .update({ kunden_layout: updatedKundenLayout })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving rent-timeline illustration:', err);
              }
            }}
          />
        );

      case 'financing-pyramid':
        return (
          <FinancingPyramid
            title={layout.title}
            layers={layout.layers || [
              { label: 'Eigenmittel', value: formValues.aktuelle_eigenmittel },
              { label: 'Förderungen', value: formValues.foerderungen },
              { label: 'Finanzierung', value: formValues.finanzierung },
            ]}
            animated={layout.animated !== false}
          />
        );

      case 'benefit-checklist':
        return (
          <BenefitChecklist
            title={layout.title}
            subtitle={layout.description}
            leftImageUrl={layout.leftImageUrl}
            rightImageUrl={layout.rightImageUrl || layout.illustrationUrl}
            isEditMode={isEditModeActive}
            onLeftImageChange={async (url) => {
              try {
                const updatedKundenLayout = {
                  ...layout,
                  leftImageUrl: url,
                };

                await fromTable('beratung_seiten')
                  .update({ kunden_layout: updatedKundenLayout })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving left health illustration:', err);
              }
            }}
            onRightImageChange={async (url) => {
              try {
                const updatedKundenLayout = {
                  ...layout,
                  rightImageUrl: url,
                };

                await fromTable('beratung_seiten')
                  .update({ kunden_layout: updatedKundenLayout })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving right health illustration:', err);
              }
            }}
          />
        );

      case 'pension-gap':
        return (
          <PensionGapChart
            data={{
              staatlich: formValues.staatliche_pension || 1200,
              betrieblich: formValues.betriebliche_vorsorge || 0,
              privat: formValues.private_vorsorge || 0,
              bedarf: formValues.pensionsbedarf || 2500,
            }}
            animated={layout.animated !== false}
          />
        );

      case 'risk-profile':
        return (
          <RiskProfileChart
            selectedProfile={formValues.risikoprofil}
            animated={layout.animated !== false}
          />
        );

      case 'lifecycle-timeline':
        return (
          <LifecycleTimeline
            title={layout.title}
            phases={layout.phases}
            currentAge={formValues.alter}
            animated={layout.animated !== false}
          />
        );

      case 'income-value':
        return (
          <IncomeValueCalculator
            monthlyIncome={formValues.nettoeinkommen || formValues.monatseinkommen}
            age={formValues.alter}
            retirementAge={layout.retirementAge || 65}
            animated={layout.animated !== false}
          />
        );

      case 'signature-panel':
        return (
          <SignaturePanel
            customerName={formValues.kundenname || `${formValues.vorname || ''} ${formValues.nachname || ''}`.trim()}
            privacyAccepted={formValues.datenschutz_akzeptiert}
            animated={layout.animated !== false}
          />
        );

      case 'feedback':
        return (
          <FeedbackRequest
            rating={formValues.zufriedenheit}
            animated={layout.animated !== false}
          />
        );

      case 'savings-comparison':
        return (
          <SavingsComparisonChart
            title={layout.title}
            monatlicheEinzahlung={formValues.monatlichBetrag || 100}
            laufzeitJahre={18}
            animated={layout.animated !== false}
          />
        );

      case 'generational-ratio':
        return (
          <GenerationalRatioChart
            title={layout.title}
            animated={layout.animated !== false}
          />
        );

      case 'investment-profile':
        return (
          <InvestmentProfileSelector
            title={layout.title}
            value={formValues.risikobereitschaft ? 
              ['sicherheit', 'konservativ', 'ausgewogen', 'wachstum', 'chancen'][formValues.risikobereitschaft - 1] 
              : 'ausgewogen'}
            animated={layout.animated !== false}
          />
        );

      case 'workforce-value':
        return (
          <WorkforceValueDisplay
            title={layout.title}
            nettoEinkommen={formValues.nettoeinkommen || 2500}
            gehaelterProJahr={formValues.gehaelter || 14}
            arbeitsjahre={formValues.arbeitsjahre || 30}
            animated={layout.animated !== false}
          />
        );

      case 'child-provision':
        return (
          <ChildProvisionChart
            monatlicheEinzahlung={formValues.kindervorsorge_einzahlung ?? layout.monatlicheEinzahlung ?? 100}
            laufzeitJahre={formValues.kindervorsorge_jahre ?? layout.laufzeitJahre ?? 25}
            illustrationUrl={layout.illustrationUrl}
            isEditMode={isEditModeActive}
            onFieldChange={onFieldChange}
            onIllustrationChange={async (url) => {
              try {
                const updatedKundenLayout = {
                  ...layout,
                  illustrationUrl: url,
                };

                await fromTable('beratung_seiten')
                  .update({ kunden_layout: updatedKundenLayout })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving child-provision illustration:', err);
              }
            }}
          />
        );

      case 'retirement-planning':
        return (
          <RetirementPlanningView
            leftImageUrl={layout.leftImageUrl}
            rightImageUrl={layout.rightImageUrl}
            isEditMode={isEditModeActive}
            onLeftImageChange={async (url) => {
              try {
                const updatedKundenLayout = {
                  ...layout,
                  leftImageUrl: url,
                };

                await fromTable('beratung_seiten')
                  .update({ kunden_layout: updatedKundenLayout })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving left retirement illustration:', err);
              }
            }}
            onRightImageChange={async (url) => {
              try {
                const updatedKundenLayout = {
                  ...layout,
                  rightImageUrl: url,
                };

                await fromTable('beratung_seiten')
                  .update({ kunden_layout: updatedKundenLayout })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving right retirement illustration:', err);
              }
            }}
          />
        );

      case 'investment-view':
        return (
          <InvestmentView
            leftImageUrl={layout.leftImageUrl}
            rightImageUrl={layout.rightImageUrl}
            isEditMode={isEditModeActive}
            onLeftImageChange={async (url) => {
              try {
                await fromTable('beratung_seiten')
                  .update({ kunden_layout: { ...layout, leftImageUrl: url } })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving investment left illustration:', err);
              }
            }}
            onRightImageChange={async (url) => {
              try {
                await fromTable('beratung_seiten')
                  .update({ kunden_layout: { ...layout, rightImageUrl: url } })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving investment right illustration:', err);
              }
            }}
          />
        );

      case 'household-budget':
        return (
          <HouseholdBudgetView
            leftImageUrl={layout.leftImageUrl}
            isEditMode={isEditModeActive}
            onLeftImageChange={async (url) => {
              try {
                await fromTable('beratung_seiten')
                  .update({ kunden_layout: { ...layout, leftImageUrl: url } })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving household budget illustration:', err);
              }
            }}
            formValues={formValues}
            onFieldChange={onFieldChange}
          />
        );

      case 'property-insurance':
        return (
          <PropertyInsuranceView
            illustrationUrl={layout.illustrationUrl}
            isEditMode={isEditModeActive}
            onIllustrationChange={async (url) => {
              try {
                await fromTable('beratung_seiten')
                  .update({ kunden_layout: { ...layout, illustrationUrl: url } })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving property insurance illustration:', err);
              }
            }}
          />
        );

      case 'income-protection':
        return (
          <IncomeProtectionView
            illustrationUrl={layout.illustrationUrl}
            isEditMode={isEditModeActive}
            onIllustrationChange={async (url) => {
              try {
                await fromTable('beratung_seiten')
                  .update({ kunden_layout: { ...layout, illustrationUrl: url } })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving income protection illustration:', err);
              }
            }}
            formValues={formValues}
            onFieldChange={onFieldChange}
          />
        );

      case 'referral-feedback':
        return (
          <ReferralFeedbackView
            illustrationUrl={layout.illustrationUrl}
            isEditMode={isEditModeActive}
            onIllustrationChange={async (url) => {
              try {
                await fromTable('beratung_seiten')
                  .update({ kunden_layout: { ...layout, illustrationUrl: url } })
                  .eq('id', seite.id);
              } catch (err) {
                console.error('Error saving referral feedback illustration:', err);
              }
            }}
          />
        );

      default:
        return (
          <div className="h-48 bg-gradient-to-br from-muted to-muted/50 rounded-xl flex items-center justify-center">
            {IconComponent && <IconComponent className="h-16 w-16 text-muted-foreground/40" />}
          </div>
        );
    }
  };

  // Show popover only in popover mode
  const showDrawingPopover = drawingPlacement === 'popover' && isDrawingShared && !isDrawingDismissed && drawingContent && onDrawingChange;
  
  // Show inline drawing instead of visualization
  const showInlineDrawing = drawingPlacement === 'inline' && isDrawingShared && drawingContent;

  return (
    <div ref={containerRef} className={cn(
      "h-full flex flex-col relative",
      "bg-muted/30",
      isFullscreen && "bg-background"
    )}>
      {/* Header - show if not fullscreen OR if showHeader is true */}
      {(!fullscreen || showHeader || isFullscreen) && (
        <div className="p-4 border-b bg-background shrink-0 flex items-center justify-between">
          <h2 className="font-semibold">Kunden-Ansicht</h2>
          <Button
            variant="outline"
            size="icon"
            onClick={toggleFullscreen}
            className="h-8 w-8"
            title={isFullscreen ? "Vollbild beenden" : "Vollbild"}
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}
      <div className={cn(
        "flex-1 min-h-0 flex flex-col overflow-y-auto p-4"
      )}>
        {/* Always render with card structure for consistency */}
        <Card className="border-none shadow-lg flex-1 flex flex-col min-h-0">
          {/* Hide CardHeader for modules with embedded titles */}
          {!['priority-tiles', 'rent-timeline', 'child-provision', 'benefit-checklist', 'retirement-planning', 'household-budget', 'property-insurance', 'income-protection', 'referral-feedback'].includes(layout.type) && (
            <CardHeader className="text-center shrink-0">
              <CardTitle className="text-xl">
                {showInlineDrawing ? 'Skizze vom Berater' : layout.title}
              </CardTitle>
            </CardHeader>
          )}
          <CardContent className={cn(
            "flex-1 min-h-0 overflow-y-auto",
            fullscreen && "flex items-center justify-center"
          )}>
            {showInlineDrawing ? (
              <div className={cn(fullscreen ? "w-full h-full" : "min-h-[400px]")}>
                <DrawingCanvas
                  content={drawingContent!}
                  onChange={onDrawingChange ?? (() => {})}
                  readonly={!isDrawingCollaborative}
                  showControls={false}
                  fullscreen={fullscreen}
                />
              </div>
            ) : (
              <div className={cn(fullscreen ? "w-full h-full flex items-center justify-center" : "")}>
                {renderVisualization()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Drawing Popover - centered by default, draggable, closable */}
      {showDrawingPopover && (
        <div 
          className={cn(
            "absolute z-50 bg-background border rounded-lg shadow-2xl transition-all",
            isDrawingExpanded 
              ? "inset-4" 
              : "w-[400px] max-h-[400px]",
            isDragging ? "cursor-grabbing" : ""
          )}
          style={isDrawingExpanded ? undefined : {
            left: '50%',
            top: '50%',
            transform: `translate(calc(-50% + ${drawingPosition.x}px), calc(-50% + ${drawingPosition.y}px))`,
          }}
        >
          {/* Popover Header - Draggable */}
          <div 
            className={cn(
              "flex items-center justify-between p-3 border-b bg-muted/50 rounded-t-lg select-none",
              !isDrawingExpanded && "cursor-grab"
            )}
            onPointerDown={!isDrawingExpanded ? handleDragStart : undefined}
            onPointerMove={!isDrawingExpanded ? handleDrag : undefined}
            onPointerUp={!isDrawingExpanded ? handleDragEnd : undefined}
            onPointerCancel={!isDrawingExpanded ? handleDragEnd : undefined}
          >
            <div className="flex items-center gap-2">
              {!isDrawingExpanded && (
                <GripHorizontal className="h-4 w-4 text-muted-foreground" />
              )}
              <Pencil className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Skizze vom Berater</span>
              {isDrawingCollaborative && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  Kollaboration aktiv
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setIsDrawingExpanded(!isDrawingExpanded)}
              >
                {isDrawingExpanded ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setIsDrawingDismissed(true)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Drawing Canvas */}
          <div className={cn(
            "p-3 overflow-y-auto",
            isDrawingExpanded ? "h-[calc(100%-48px)]" : "max-h-[300px]"
          )}>
            <DrawingCanvas
              content={drawingContent}
              onChange={onDrawingChange}
              readonly={!isDrawingCollaborative}
              showControls={isDrawingCollaborative}
              compact={!isDrawingExpanded}
            />
          </div>
        </div>
      )}
    </div>
  );
}
